import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import { 
  Music, Home, FileText, CreditCard,
  ChevronLeft, ChevronRight, Bell, Settings, LogOut,
  ShoppingBag, ClipboardList, Calendar
} from 'lucide-react';
import './App.css';

import { users, defaultCredentials, roles } from './data/mockData';

import Dashboard from './pages/Dashboard';
import Produits from './pages/Produits';
import Inscriptions from './pages/Inscriptions';
import Seances from './pages/Seances';
import Facturation from './pages/Facturation';
import Paiements from './pages/Paiements';
import Login from './pages/Login';
import InscriptionPublique from './pages/InscriptionPublique';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const ROLE_ROUTES = {
  coordinateur: ['/', '/inscriptions', '/produits', '/seances', '/facturation', '/paiements'],
  tresoriere:   ['/facturation', '/paiements'],
  professeur:   ['/seances'],
  president:    ['/'],
  ca_membre:    ['/'],
};

const ROLE_DEFAULT = {
  coordinateur: '/',
  tresoriere:   '/facturation',
  professeur:   '/seances',
  president:    '/',
  ca_membre:    '/',
};

const ALL_NAV_ITEMS = [
  { section: 'Principal' },
  { path: '/', icon: Home, label: 'Tableau de bord' },
  
  { section: 'Gestion' },
  { path: '/inscriptions', icon: ClipboardList, label: 'Inscriptions' },
  { path: '/produits', icon: ShoppingBag, label: 'Produits' },
  { path: '/seances', icon: Calendar, label: 'Séances' },
  
  { section: 'Comptabilité' },
  { path: '/facturation', icon: FileText, label: 'Facturation' },
  { path: '/paiements', icon: CreditCard, label: 'Paiements & Relances' },
];

const Navigation = ({ isCollapsed, setIsCollapsed, currentUser, onLogout }) => {
  const location = useLocation();
  const userRole = roles.find(r => r.id === currentUser?.role);
  const allowedRoutes = ROLE_ROUTES[currentUser?.role] || [];

  const filteredNavItems = ALL_NAV_ITEMS.filter(item => {
    if (item.section) return true;
    return allowedRoutes.includes(item.path);
  }).filter((item, index, arr) => {
    if (!item.section) return true;
    const next = arr[index + 1];
    return next && !next.section;
  });

  return (
    <nav className={`navigation ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="nav-header">
        <div className="nav-brand">
          <Music className="brand-icon" />
          {!isCollapsed && <h1>Harmonie</h1>}
        </div>
        <button className="nav-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="nav-user">
          <div className="user-avatar" style={{ background: currentUser?.color || '#8B5CF6' }}>
            {currentUser?.initials || 'AD'}
          </div>
          <div className="user-info">
            <p className="user-name">{currentUser?.prenom} {currentUser?.nom}</p>
            <p className="user-role">{userRole?.label || 'Utilisateur'}</p>
          </div>
        </div>
      )}

      <ul className="nav-menu">
        {filteredNavItems.map((item, index) => {
          if (item.section) {
            return !isCollapsed && (
              <li key={index} className="nav-section">{item.section}</li>
            );
          }
          const Icon = item.icon;
          return (
            <li key={index}>
              <NavLink
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive && location.pathname === item.path ? 'active' : ''}`}
                title={isCollapsed ? item.label : ''}
              >
                <Icon size={20} />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>

      <div className="nav-footer">
        {!isCollapsed && (
          <>
            <div className="nav-footer-actions">
              <button className="btn-icon" title="Notifications"><Bell size={18} /></button>
              <button className="btn-icon" title="Paramètres"><Settings size={18} /></button>
              <button className="btn-icon" title="Déconnexion" onClick={onLogout}><LogOut size={18} /></button>
            </div>
            <div className="nav-footer-info">
              <p>Année scolaire</p>
              <strong>2025-2026</strong>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

function AdminLayout({ currentUser, onLogout }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const allowedRoutes = ROLE_ROUTES[currentUser?.role] || [];
  const defaultRoute = ROLE_DEFAULT[currentUser?.role] || '/';

  const isAllowed = (path) => allowedRoutes.includes(path);

  return (
    <div className="app">
      <Navigation 
        isCollapsed={sidebarCollapsed} 
        setIsCollapsed={setSidebarCollapsed} 
        currentUser={currentUser}
        onLogout={onLogout}
      />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-closed' : ''}`}>
        <Routes>
          {isAllowed('/') && <Route path="/" element={<Dashboard />} />}
          {isAllowed('/inscriptions') && <Route path="/inscriptions" element={<Inscriptions />} />}
          {isAllowed('/produits') && <Route path="/produits" element={<Produits />} />}
          {isAllowed('/seances') && <Route path="/seances" element={<Seances />} />}
          {isAllowed('/facturation') && <Route path="/facturation" element={<Facturation />} />}
          {isAllowed('/paiements') && <Route path="/paiements" element={<Paiements />} />}
          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);

  const handleLogin = (login, password) => {
    const cred = defaultCredentials.find(c => c.login === login && c.password === password);
    if (cred) {
      const user = users.find(u => u.id === cred.userId);
      setCurrentUser(user);
      return true;
    }
    return false;
  };

  const handleLogout = () => setCurrentUser(null);

  return (
    <AuthContext.Provider value={{ currentUser, handleLogin, handleLogout }}>
      <Router>
        <Routes>
          <Route path="/inscription" element={<InscriptionPublique />} />
          <Route path="/login" element={
            currentUser ? <Navigate to={ROLE_DEFAULT[currentUser.role] || '/'} replace /> : <Login />
          } />
          <Route path="/*" element={
            currentUser 
              ? <AdminLayout currentUser={currentUser} onLogout={handleLogout} />
              : <Navigate to="/login" replace />
          } />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
